export const ANALYSIS_FINISHED_STATUS = 6;

export type AnalysisStatusVariant = 'success' | 'info' | 'warning' | 'danger';

export type AnalysisStatusMeta = {
  label: string;
  description?: string;
  variant: AnalysisStatusVariant;
  icon: string;
};

const IN_PROGRESS_STATUSES = new Set([0, 2, 4]);

const ANALYSIS_STATUS_META: Record<number, AnalysisStatusMeta> = {
  0: {
    label: 'شروع شده',
    description: 'واکاوی شروع شده و به‌زودی وارد صف بررسی می‌شود.',
    variant: 'info',
    icon: 'stream'
  },
  2: {
    label: 'در صف بررسی',
    description: 'واکاوی در صف انتظار است. لطفاً چند لحظه صبر کنید تا بررسی شروع شود.',
    variant: 'info',
    icon: 'stream'
  },
  3: {
    label: 'خطای ناشناخته',
    description:
      'خطای نامشخص هنگام بررسی. دوباره تلاش کنید یا سرور را تغییر دهید.',
    variant: 'danger',
    icon: 'ban'
  },
  4: {
    label: 'درحال بررسی',
    description: 'واکاوی در حال انجام است. بعد از اتمام، نتیجه همین‌جا نمایش داده می‌شود.',
    variant: 'info',
    icon: 'stream'
  },
  6: {
    label: 'پایان (موفق)',
    variant: 'success',
    icon: 'check'
  },
  31: {
    label: 'خطای Timeout',
    description:
      'بعد از ۳۰ ثانیه سرور سایت شما پاسخ نداد، ممکنه سرور فعلی رو بلاک کرده باشه، پیشنهاد میشه لوکیشن سرور رو عوض کن، یا ممکنه آدرس شما ۲۰۰ نباشه و ریدایرکت به یک آدرس دیگه صورت بگیره.',
    variant: 'danger',
    icon: 'ban'
  },
  32: {
    label: 'خطای دامنه اشتباه',
    description:
      'به احتمال زیاد چنین دامنه ای وجود نداره و پیشنهاد میشه از نظر املایی دامنه رو بررسی کنید یا ربات‌های میزفا تولز دسترسی به این دامنه نداره.',
    variant: 'danger',
    icon: 'ban'
  },
  33: {
    label: 'خطای SSL',
    description:
      'به احتمال زیاد گواهی نامه SSL این دامنه مشکل داره، پیشنهاد میشه یک بار دامنه رو با http وارد کنید و https نباشه.',
    variant: 'danger',
    icon: 'ban'
  },
  34: {
    label: 'خطای Connection',
    description:
      'به احتمال زیاد چنین دامنه ای وجود نداره و پیشنهاد میشه از نظر املایی دامنه رو بررسی کنید و آدرس دامنه رو درست وارد کنید، یا ربات‌های میزفا تولز دسترسی به این دامنه رو نداره، و در این حالت لوکیشن سرور بررسی صفحه رو عوض کنید.',
    variant: 'danger',
    icon: 'ban'
  },
  35: {
    label: 'آدرس نامعتبر برای بررسی',
    description:
      'لطفا آدرس معتبر ارسال کنید، توجه داشته باشید که امکان بررسی فایل‌ها مثل PDF وجود نداره.',
    variant: 'danger',
    icon: 'ban'
  },
  36: {
    label: 'خطای ۴۰۳',
    description:
      'سرور سایت شما دسترسی را رد کرده (Forbidden). ممکن است فایروال، WAF یا محدودیت IP ربات‌های میزفا تولز را مسدود کرده باشد؛ لوکیشن سرور بررسی را عوض کنید یا دسترسی ربات‌ها را بررسی کنید.',
    variant: 'danger',
    icon: 'ban'
  },
  37: {
    label: 'خطای لایت‌هاوس',
    description:
      'بررسی لایت‌هاوس قبل از ساخت گزارش با خطا متوقف شد. لوکیشن سرور را عوض کنید یا دوباره تلاش کنید.',
    variant: 'danger',
    icon: 'ban'
  },
  49: {
    label: 'متوقف شده',
    description: 'این واکاوی متوقف شده و نتیجه‌ای برای نمایش وجود ندارد.',
    variant: 'warning',
    icon: 'pause'
  }
};

const FALLBACK_STATUS_META: AnalysisStatusMeta = {
  label: 'واکاوی کامل نشده',
  description:
    'نتیجه این واکاوی هنوز آماده نیست یا با خطا مواجه شده است. می‌توانید دوباره بررسی را شروع کنید.',
  variant: 'warning',
  icon: 'exclamation-circle'
};

export function parseAnalysisStatus(status: unknown): number | undefined {
  if (status == null || status === '') return undefined;
  const numeric = Number(status);
  return Number.isFinite(numeric) ? numeric : undefined;
}

export function isAnalysisFinished(status: unknown): boolean {
  const numeric = parseAnalysisStatus(status);
  return numeric === undefined || numeric === ANALYSIS_FINISHED_STATUS;
}

export function isAnalysisInProgress(status: unknown): boolean {
  const numeric = parseAnalysisStatus(status);
  return numeric !== undefined && IN_PROGRESS_STATUSES.has(numeric);
}

export function getAnalysisStatusMeta(status: unknown): AnalysisStatusMeta {
  const numeric = parseAnalysisStatus(status);
  if (numeric === undefined) return FALLBACK_STATUS_META;
  return ANALYSIS_STATUS_META[numeric] || FALLBACK_STATUS_META;
}

export function hasKnownAnalysisStatus(status: unknown): boolean {
  const numeric = parseAnalysisStatus(status);
  return numeric !== undefined && Boolean(ANALYSIS_STATUS_META[numeric]);
}

